// Relay access (upload/download blobs, publish a public key) is gated by an
// API token instead of a login. Anyone can request one with no identity
// check via POST /api-tokens; it's short-lived (~1hr) and passed as
// X-Api-Key on every relay call. Held in memory only - a reload means
// requesting a new one, same as we do for private keys. Also schedules an
// "about to expire" warning.
import { showStickyToast, resolveStickyToast } from "../components/toast";

const TOKEN_WARNING_KEY = "api-token-expiry";
// How far ahead of expiry to warn. Overridable via
// window.__SC_TOKEN_WARNING_LEAD_MS__ so tests can trigger the warning
// without waiting out a real ~1hr token.
function warningLeadMs(): number {
  const override = (globalThis as any).__SC_TOKEN_WARNING_LEAD_MS__;
  return typeof override === "number" ? override : 5 * 60 * 1000;
}

export interface ApiTokenState {
  token: string | null;
  expiresAt: number | null; // epoch seconds
}

export const apiTokenState: ApiTokenState = { token: null, expiresAt: null };

let warningTimer: ReturnType<typeof setTimeout> | undefined;
// Lets the sidebar token widget react to changes instead of polling
// apiTokenState.
const listeners = new Set<() => void>();

export function onApiTokenChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function setApiToken(token: string, expiresAtEpochSeconds: number): void {
  apiTokenState.token = token;
  apiTokenState.expiresAt = expiresAtEpochSeconds;
  resolveStickyToast(TOKEN_WARNING_KEY);
  scheduleWarning();
  notify();
}

export function clearApiToken(): void {
  apiTokenState.token = null;
  apiTokenState.expiresAt = null;
  resolveStickyToast(TOKEN_WARNING_KEY);
  if (warningTimer !== undefined) clearTimeout(warningTimer);
  notify();
}

function scheduleWarning(): void {
  if (warningTimer !== undefined) clearTimeout(warningTimer);
  if (apiTokenState.expiresAt === null) return;
  const msUntilWarning = apiTokenState.expiresAt * 1000 - Date.now() - warningLeadMs();
  warningTimer = setTimeout(showExpiryWarning, Math.max(0, msUntilWarning));
}

function showExpiryWarning(): void {
  if (apiTokenState.expiresAt === null) return;
  showStickyToast(
    TOKEN_WARNING_KEY,
    "Your API token will expire soon. Request a new one to keep uploading and downloading files - this never affects encryption itself, which runs entirely in your browser regardless of the token.",
  );
}
